import api from './api'
import { Oportunidade } from '@/types/oportunidade'
import { ReclassificacaoProduto, ReclassificarProdutoRequest } from '@/types/reclassificacao'

const BASE_URL = '/api/v1/oportunidades'

// ─── P1 (AN-PP1 §7) — chamadas novas, backend DEV-PP1-O em paralelo ───
//
// Isoladas aqui para o ajuste da integração: caminhos e formas são os do desenho (§7.1/§7.3).
// O backend de oportunidades NÃO embrulha respostas em ApiResponse — o interceptor de `api` já
// devolve o corpo.
export const reclassificacaoService = {
  /**
   * `POST /v1/oportunidades/{id}/reclassificar-produto` — back-office (ADMIN, GERENTE, OPERADOR
   * sem escopo de dono). Devolve a reserva com o produto novo.
   */
  reclassificar: async (oportunidadeId: string, request: ReclassificarProdutoRequest): Promise<Oportunidade> => {
    const response = await api.post<Oportunidade>(
      `${BASE_URL}/${encodeURIComponent(oportunidadeId)}/reclassificar-produto`,
      request
    )
    return response as unknown as Oportunidade
  },

  /**
   * `GET /v1/oportunidades/{id}/reclassificacoes` — back-office global; consultor dono lê sem o
   * autor; consultor de outra carteira recebe 404.
   */
  listar: async (oportunidadeId: string): Promise<ReclassificacaoProduto[]> => {
    const response = await api.get<ReclassificacaoProduto[]>(
      `${BASE_URL}/${encodeURIComponent(oportunidadeId)}/reclassificacoes`
    )
    return (response as unknown as ReclassificacaoProduto[]) ?? []
  },
}

/** Mensagens legíveis dos códigos do §7.1 (o 403 nem chega: a ação é escondida do consultor). */
export const MENSAGEM_ERRO_RECLASSIFICACAO: Record<string, string> = {
  RESERVA_NAO_E_OUTRA_NECESSIDADE:
    'Esta reserva já tem um produto do catálogo — só "Outra necessidade" pode ser reclassificada.',
  RECLASSIFICACAO_NAO_PERMITIDA_NO_STATUS:
    'A reclassificação só é permitida de "Aguardando aprovação" até "Aprovada Originei". Nada foi alterado.',
  PRODUTO_INATIVO: 'O produto escolhido não está disponível para reservas. Escolha outro produto.',
  PRODUTO_DESCONHECIDO:
    'Não foi possível confirmar o produto escolhido no catálogo agora. Tente em instantes ou escolha outro.',
  MOTIVO_OBRIGATORIO: 'Informe o motivo da reclassificação (até 500 caracteres).',
  // Integração P1: códigos que o backend (ReclassificacaoProdutoService) também devolve.
  PRODUTO_OBRIGATORIO: 'Escolha o produto do catálogo para a reclassificação.',
  PRODUTO_DESTINO_INVALIDO: 'Escolha um produto do catálogo diferente de "Outra necessidade".',
  RESERVA_DUPLICADA:
    'Este CNPJ já possui uma reserva ativa para o produto escolhido. Nada foi alterado.',
  // Fix P1 (B5): produto de origem projetado antes do código canônico.
  CATALOGO_NAO_SINCRONIZADO:
    'O catálogo de produtos ainda não foi sincronizado para esta reserva. Peça ao administrador a ' +
    'ressincronização de produtos e tente de novo. Nada foi alterado.',
}

export const mensagemErroReclassificacao = (e: unknown): string => {
  const falha = e as { data?: { errorCode?: unknown }; message?: unknown; status?: number } | null
  const codigo = falha?.data?.errorCode
  if (typeof codigo === 'string' && MENSAGEM_ERRO_RECLASSIFICACAO[codigo]) return MENSAGEM_ERRO_RECLASSIFICACAO[codigo]
  if (falha?.status === 403) return 'Você não tem permissão para reclassificar o produto desta reserva.'
  return typeof falha?.message === 'string' && falha.message
    ? falha.message
    : 'Não foi possível reclassificar o produto.'
}
